// AI の話し始め・話し終えを決める純粋な状態機械 (spec 2026-09-14 L11 / §3.7)。
//
// **出力の文字起こしは音声より 0.3〜2.8 秒先に届く** (docs/measurements/2026-09-14-gpt-live-
// timing-reanalysis.md、n=2,506) ので、文字起こしの途切れだけで「話し終えた」と判定すると、
// 読み上げの途中で次を頼んでしまう。受信音声の音量を主にし、文字起こしは従にする。
// 音量が取れない環境 (null が続く) では、文字起こしが 3 秒来なければ話し終えたとみなす
// (同じ発話の中の到着間隔は最大 1.6 秒)。
//
// 時刻は呼び出し側が渡す (テストで時間を進めるため)。

/** 声とみなす受信音量 (約 -40 dBFS)。spike の出力の無音フレームは最大 -47 dBFS、声は最大 -19 dBFS */
export const VOICE_LEVEL = 0.01;
/** 声がこれだけ途切れたら話し終え (音量が取れるとき) */
export const LEVEL_QUIET_MS = 1200;
/** 文字起こしもこれだけ来ていないこと (音量が取れるとき。文と文の間の保険) */
export const TRANSCRIPT_QUIET_MS = 1200;
/** 音量が取れないとき、文字起こしがこれだけ来なければ話し終え */
export const TRANSCRIPT_ONLY_QUIET_MS = 3000;

export type AiSpeechEvent = "start" | "end" | null;

export class AiSpeechTracker {
  private speaking = false;
  private levelsAvailable = false;
  private lastVoiceAt = Number.NEGATIVE_INFINITY;
  private lastTranscriptAt = Number.NEGATIVE_INFINITY;

  isSpeaking(): boolean {
    return this.speaking;
  }

  /** 受信音量 (0..1)。null は「取れない」 */
  feedLevel(level: number | null, now: number): AiSpeechEvent {
    if (level === null) return this.tick(now);
    this.levelsAvailable = true;
    if (level > VOICE_LEVEL) {
      this.lastVoiceAt = now;
      if (this.speaking) return null;
      this.speaking = true;
      return "start";
    }
    return this.tick(now);
  }

  /** 出力の文字起こしが届いた */
  feedTranscript(now: number): AiSpeechEvent {
    this.lastTranscriptAt = now;
    if (this.speaking) return null;
    this.speaking = true;
    return "start";
  }

  /** 時間の経過。話し終えたら "end" を 1 回だけ返す */
  tick(now: number): AiSpeechEvent {
    if (!this.speaking) return null;
    const ended = this.levelsAvailable
      ? now - this.lastVoiceAt >= LEVEL_QUIET_MS &&
        now - this.lastTranscriptAt >= TRANSCRIPT_QUIET_MS
      : now - this.lastTranscriptAt >= TRANSCRIPT_ONLY_QUIET_MS;
    if (!ended) return null;
    this.speaking = false;
    return "end";
  }

  reset(): void {
    this.speaking = false;
    this.lastVoiceAt = Number.NEGATIVE_INFINITY;
    this.lastTranscriptAt = Number.NEGATIVE_INFINITY;
  }
}
